'use client';

import { Button } from '@packages/shared/src/ui/Button/Button';
import { useId, useRef, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { infoIconButtonStyles, infoIconStyles, tooltipContainerStyles } from './Tooltip.styles';
import type { TooltipProps } from './TooltipProps';
import { TooltipWrapper } from './TooltipWrapper';
import { toggleTooltip } from './utils/toggleTooltip';
import { useFixPositionOnScroll } from './utils/useFixPositionOnScroll';

const messageMoreInformation = defineMessage({
  id: 'navigation.tooltip.cms.back.aria-label',
  defaultMessage: 'Weitere Informationen anzeigen',
});

const Tooltip = ({
  children /* the container the tooltip belongs to */,
  content: tooltipContent,
  variant,
  positioning = 'top-start',
}: TooltipProps) => {
  const positioningInfoIcon = variant === 'inline' ? null : positioning;
  const tooltipId = useId();
  const { formatMessage } = useIntl();
  const tooltipContentRef = useRef<HTMLSpanElement>(null);
  /* this state is used to store the current positioning of the tooltip (modified by the intersection observer and the initial click to open).
    It is initialized with undefined to indicate the tooltip should not be visible */
  const [positioningTooltip, setPositioningTooltip] = useState<TooltipProps['positioning']>();

  // on vertical scroll toggle the positioning of the tooltip if it is intersecting
  useFixPositionOnScroll(tooltipContentRef, setPositioningTooltip);

  if (!tooltipContent) {
    return children;
  }

  return (
    <TooltipWrapper
      setPositioningTooltip={setPositioningTooltip}
      variant={variant}
      positioning={positioning}
      contentChildren={children}
    >
      <div
        key="tooltipContent"
        className={infoIconStyles({ variant, positioningInfoIcon, positioningTooltip })}
      >
        {/* this is the "I" letter circle background */}
        <Button
          aria-label={formatMessage(messageMoreInformation)}
          aria-controls={tooltipId}
          aria-expanded={!!positioningTooltip}
          className={infoIconButtonStyles(!!positioningTooltip)}
          variant="icon-flat"
          size="small"
          onClick={toggleTooltip({
            tooltipContentRef,
            setPositioningTooltip,
            variant,
            positioning,
          })}
        >
          {/* this is the "I" letter only */}
          <span className="icon-emp-info bg-color-info-dark hover:bg-color-info-light" />
        </Button>
        <span
          hidden={!positioningTooltip || undefined}
          ref={tooltipContentRef}
          id={tooltipId}
          className={tooltipContainerStyles(positioningTooltip)}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: tooltipContent }}
        />
      </div>
    </TooltipWrapper>
  );
};

Tooltip.displayName = 'Tooltip';

export { Tooltip };
